/**
 * Kompresija slik na klientu za zanesljivo nalaganje na mobitelu.
 * - Skalira dimenzije, da najdaljša stranica ni večja od maxDim
 * - Kompresira v JPEG s kakovostjo quality (0..1)
 * - Ohrani File API (return File) da se lahko FormData.append normalno uporabi
 * - Sprejme HEIC/HEIF preko browser dekodiranja (iOS Safari to podpira)
 *
 * Vrne originalno datoteko, če je že manjša od `skipIfSmallerThan` bajtov,
 * da se izognemo nepotrebnemu dekodiranju že primerno majhne slike.
 */
export async function compressImage(file, {
  maxDim = 2000,
  quality = 0.82,
  skipIfSmallerThan = 800 * 1024, // 800 KB
  outputMime = "image/jpeg",
} = {}) {
  if (!file || !(file instanceof Blob)) return file;

  // Če slika ni razpoznavna kot image, vrni original brez kompresije.
  if (file.type && !file.type.startsWith("image/")) return file;

  // Zelo majhne slike vrni nespremenjeno.
  if (file.size && file.size < skipIfSmallerThan) return file;

  // Pridobi bitmap; createImageBitmap je najhitrejša pot in podpira EXIF orientation.
  let bitmap;
  try {
    // eslint-disable-next-line no-undef
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch (_e) {
    // Fallback prek <img> in URL.createObjectURL
    bitmap = await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
      img.src = url;
    });
  }

  const srcW = bitmap.width;
  const srcH = bitmap.height;
  if (!srcW || !srcH) return file;

  const scale = Math.min(1, maxDim / Math.max(srcW, srcH));
  const dstW = Math.round(srcW * scale);
  const dstH = Math.round(srcH * scale);

  const canvas = document.createElement("canvas");
  canvas.width = dstW;
  canvas.height = dstH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, dstW, dstH);

  // Free memory
  if (bitmap.close) bitmap.close();

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, outputMime, quality);
  });
  if (!blob) return file;

  // Ohrani ime, samo zamenjaj končnico če izhod JPEG
  const baseName = (file.name || "slika").replace(/\.[^.]+$/i, "");
  const ext = outputMime === "image/jpeg" ? "jpg" : "png";
  return new File([blob], `${baseName}.${ext}`, { type: outputMime, lastModified: Date.now() });
}
